import {DataTourAnchorEnum} from '@rdcomponents/usertour/data-tour-anchor';

import {AdditionalInfo} from '../../../../../../layout/additional-info/additional-info';
import {useBrandValidationSettings} from '../../../../../../provider/brands/brand-validation-settings';
import {CatalogConfigType} from '../../../../../../provider/catalogs/catalogs-type';
import {useDomainConfig} from '../../../../../../provider/domain-config/domain-config-hook';
import {useDocumentationLinks} from '../../../../../../provider/help-links/help-links-hook';
import {useLocale} from '../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../provider/locale/localization';
import {CompanyKeyEnum, ProtocolEnum} from '../../../../../../service/company-v2/company-const';
import {useRegularSocialsApi} from '../../../../../../service/company-v2/company-hook';
import {CompanyFormType} from '../../../../../../service/company-v2/company-type';
import {CompanyFormSocialNetworkType} from '../../../../../../service/company-v2/types/company-links-type';
import {Form} from '../../../../../../typings/antd';
import {ProvidersIdsEnum} from '../../../../../../util/type';
import {CatalogConfigWithIndexType, CatalogList} from '../../catalog-list/catalog-list';
import {getErrorTabIndex} from '../../catalog-list/catalog-list-helper';
import {extractProtocol, MessengerRegexpEntryType} from '../../company-form-helper';

import {CatalogLinks} from './catalog-links';
import {LinksFormItem} from './links-form-item/links-form-item';

const FIRST_WEBSITE_ACCESSOR = [CompanyKeyEnum.Websites, 0] as [CompanyKeyEnum.Websites, 0];

type PropsType = {
    name: string;
    hasInitialSocialNetworks: boolean;
    hasInitialWebsites: boolean;
    brandId?: number | null;
    initialCatalogs?: Array<number>;
};

export function LinksWidget(props: PropsType): JSX.Element {
    const {name, hasInitialSocialNetworks, hasInitialWebsites, initialCatalogs, brandId} = props;

    const {data: brandValidationSettings} = useBrandValidationSettings(brandId);

    const {getLocalizedString} = useLocale();

    const documentationLinks = useDocumentationLinks();

    const {isShowMaxSocialNetworkPrefix} = useDomainConfig();
    const {data: socialsData} = useRegularSocialsApi();

    const messengerNameToProtocol: Partial<Record<string, ProtocolEnum>> = {
        whatsapp: ProtocolEnum.WHATSAPP,
        viber: ProtocolEnum.VIBER,
        ...(isShowMaxSocialNetworkPrefix ? {max: ProtocolEnum.MAX} : {}),
    };

    const messengerRegexps: Array<MessengerRegexpEntryType> = (socialsData ?? []).flatMap((item) => {
        const protocol = messengerNameToProtocol[item.name.toLowerCase()];

        return protocol ? [{regexp: item.regexp, protocol}] : [];
    });

    function applyMessengerProtocols(
        socialNetworks: Array<CompanyFormSocialNetworkType>
    ): Array<CompanyFormSocialNetworkType> {
        return socialNetworks.map((socialNetwork) => {
            if (socialNetwork.protocol === ProtocolEnum.MAX) {
                return socialNetwork;
            }

            const result = extractProtocol(socialNetwork.protocol + socialNetwork.value, {messengerRegexps});

            if (result?.protocol === ProtocolEnum.MAX) {
                return {...socialNetwork, protocol: ProtocolEnum.MAX};
            }

            return result ? {...socialNetwork, ...result} : socialNetwork;
        });
    }

    return (
        <>
            <div data-tour={DataTourAnchorEnum.CompanyFormLinksMain}>
                <LinksFormItem
                    hasInitialLinks={hasInitialWebsites}
                    label={getLocalizedString('COMPANY_FORM__LINKS__WEBSITES__LABEL')}
                    name={CompanyKeyEnum.Websites}
                    placeholder={getLocalizedString('COMPANY_FORM__LINKS__WEBSITES__PLACEHOLDER')}
                    required={!brandValidationSettings?.makeWebsiteOptionalField}
                />

                <LinksFormItem
                    hasInitialLinks={hasInitialSocialNetworks}
                    httpsOnly
                    label={getLocalizedString('COMPANY_FORM__LINKS__SOCIAL_NETWORKS__LABEL')}
                    name={CompanyKeyEnum.SocialNetworks}
                    placeholder={getLocalizedString('COMPANY_FORM__LINKS__SOCIAL_NETWORKS__PLACEHOLDER')}
                />
            </div>

            <Form.Item<CompanyFormType> dependencies={[FIRST_WEBSITE_ACCESSOR]} noStyle>
                {({getFieldValue, setFieldsValue, getFieldsError}) => (
                    <CatalogList
                        activeTabIndex={getErrorTabIndex(getFieldsError, [
                            CompanyKeyEnum.ExtraWebsites,
                            CompanyKeyEnum.ExtraSocialNetworks,
                        ])}
                        disabled={!getFieldValue(FIRST_WEBSITE_ACCESSOR)?.value}
                        hint={
                            <AdditionalInfo title={<Locale stringKey="COMPANY_FORM__LINKS__SOCIAL_NETWORKS__LABEL" />}>
                                <Locale
                                    stringKey="COMPANY_FORM__LINKS__SOCIAL_NETWORKS__HINT"
                                    valueMap={{
                                        link: documentationLinks && (
                                            <p>
                                                <a
                                                    href={documentationLinks.company.contactsFromCatalogs}
                                                    rel="noreferrer"
                                                    target="_blank"
                                                >
                                                    <Locale stringKey="GENERAL_INFO__HINT__SENDING_COMPANY_NAME__DETAILS" />
                                                </a>
                                            </p>
                                        ),
                                    }}
                                />
                            </AdditionalInfo>
                        }
                        initialCatalogs={initialCatalogs}
                        isCatalogVisible={({canRewriteWebsites, canRewriteSocialNetworks}) =>
                            canRewriteWebsites || canRewriteSocialNetworks
                        }
                        onAddCatalogs={(catalogs: Array<CatalogConfigType>) => {
                            const catalogIds = new Set(catalogs.map(({catalogId}) => catalogId));

                            setFieldsValue({
                                extraWebsites: [
                                    ...getFieldValue(CompanyKeyEnum.ExtraWebsites),
                                    ...catalogs.map(({catalogId}) => ({
                                        catalogId,
                                        websites: getFieldValue(CompanyKeyEnum.Websites),
                                    })),
                                ],
                                extraSocialNetworks: [
                                    ...(getFieldValue(CompanyKeyEnum.ExtraSocialNetworks) || []).filter(
                                        ({catalogId}) => !catalogIds.has(catalogId)
                                    ),
                                    ...catalogs.map(({catalogId}) => {
                                        const socialNetworks: Array<CompanyFormSocialNetworkType> = getFieldValue(
                                            CompanyKeyEnum.SocialNetworks
                                        );

                                        return {
                                            catalogId,
                                            socialNetworks:
                                                catalogId === ProvidersIdsEnum.doubleGis
                                                    ? applyMessengerProtocols(socialNetworks)
                                                    : socialNetworks,
                                        };
                                    }),
                                ],
                            });
                        }}
                        onCatalogAvailabilityChange={(availableCatalogIds: Array<number>) => {
                            setFieldsValue({
                                extraWebsites: getFieldValue(CompanyKeyEnum.ExtraWebsites).filter(({catalogId}) =>
                                    availableCatalogIds.includes(catalogId)
                                ),
                                extraSocialNetworks: getFieldValue(CompanyKeyEnum.ExtraSocialNetworks).filter(
                                    ({catalogId}) => availableCatalogIds.includes(catalogId)
                                ),
                            });
                        }}
                        renderCatalog={(catalog: CatalogConfigWithIndexType, {handleRemoveCatalog}) => (
                            <CatalogLinks
                                catalog={catalog}
                                handleRemoveCatalog={handleRemoveCatalog}
                                hasInitialSocialNetworks={Boolean(
                                    getFieldValue([CompanyKeyEnum.ExtraSocialNetworks, catalog.index, 'socialNetworks'])
                                        ?.length
                                )}
                                hasInitialWebsites={Boolean(
                                    getFieldValue([CompanyKeyEnum.ExtraWebsites, catalog.index, 'websites'])?.length
                                )}
                                name={name}
                            />
                        )}
                        title={getLocalizedString('COMPANY_FORM__LINKS__CATALOGS_HEADER')}
                    />
                )}
            </Form.Item>
        </>
    );
}
